import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context'
import { getAuthProvider } from './index'
import { can as roleCan, type Capability } from './permissions'
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from './config'
import type { ChangePasswordInput, Session, SignInInput } from './types'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const
const TICK_MS = 15_000

export function AuthProvider({ children }: { children: ReactNode }) {
  const provider = useMemo(() => getAuthProvider(), [])
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [session, setSession] = useState<Session | null>(null)
  const [idleWarningMs, setIdleWarningMs] = useState<number | null>(null)
  const [lastSignOutReason, setLastSignOutReason] = useState<'idle' | 'expired' | null>(null)

  const lastActivityRef = useRef<number>(Date.now())

  const signOut = useCallback(
    async (reason: 'user' | 'idle' | 'expired' = 'user') => {
      await (await provider).signOut()
      setSession(null)
      setStatus('anonymous')
      setIdleWarningMs(null)
      setLastSignOutReason(reason === 'user' ? null : reason)
    },
    [provider],
  )

  // Restore the session on reload.
  useEffect(() => {
    let cancelled = false
    provider
      .then((p) => p.restore())
      .then((restored) => {
        if (cancelled) return
        setSession(restored)
        setStatus(restored ? 'authenticated' : 'anonymous')
      })
      .catch(() => {
        if (cancelled) return
        setSession(null)
        setStatus('anonymous')
      })
    return () => {
      cancelled = true
    }
  }, [provider])

  // Track user activity to measure idle time.
  useEffect(() => {
    if (status !== 'authenticated') return
    const mark = () => {
      lastActivityRef.current = Date.now()
      setIdleWarningMs((prev) => (prev === null ? prev : null))
    }
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, mark, { passive: true })
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, mark)
    }
  }, [status])

  // Ticker for both absolute expiry and idle expiry.
  useEffect(() => {
    if (status !== 'authenticated' || !session) return

    const tick = () => {
      const now = Date.now()

      if (session.expiresAt <= now) {
        void signOut('expired')
        return
      }

      const idleFor = now - lastActivityRef.current
      const remaining = IDLE_TIMEOUT_MS - idleFor

      if (remaining <= 0) {
        void signOut('idle')
        return
      }
      setIdleWarningMs(remaining <= IDLE_WARNING_MS ? remaining : null)
    }

    tick()
    const id = window.setInterval(tick, TICK_MS)
    return () => window.clearInterval(id)
  }, [status, session, signOut])

  const signIn = useCallback(
    async (input: SignInInput) => {
      const next = await (await provider).signIn(input)
      lastActivityRef.current = Date.now()
      setSession(next)
      setStatus('authenticated')
      setIdleWarningMs(null)
      setLastSignOutReason(null)
    },
    [provider],
  )

  const changePassword = useCallback(
    async (input: ChangePasswordInput) => {
      const p = await provider
      await p.changePassword(input)
      const refreshed = await p.touch()
      if (refreshed) setSession(refreshed)
    },
    [provider],
  )

  const keepAlive = useCallback(() => {
    lastActivityRef.current = Date.now()
    setIdleWarningMs(null)
  }, [])

  const can = useCallback(
    (capability: Capability) => (session ? roleCan(session.user.role, capability) : false),
    [session],
  )

  const value: AuthContextValue = useMemo(
    () => ({
      status,
      session,
      idleWarningMs,
      signIn,
      signOut,
      changePassword,
      keepAlive,
      can,
      lastSignOutReason,
    }),
    [status, session, idleWarningMs, signIn, signOut, changePassword, keepAlive, can, lastSignOutReason],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
